import { forwardRef, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/services/api';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'border border-volt-300/20 bg-gradient-to-b from-volt-400 to-volt-600 text-white shadow-[0_12px_34px_-12px_rgb(46_139_255/0.95)] hover:-translate-y-0.5 hover:from-volt-300 hover:to-volt-500 hover:shadow-[0_18px_44px_-12px_rgb(46_139_255/1)] active:translate-y-0 active:from-volt-500 active:to-volt-700',
  secondary: 'border border-ink-500 bg-ink-800/75 text-steel-100 shadow-[inset_0_1px_rgb(255_255_255/0.04)] backdrop-blur hover:-translate-y-0.5 hover:border-volt-500/60 hover:bg-ink-700 hover:text-white',
  ghost: 'text-steel-200 hover:bg-ink-800 hover:text-white',
  danger: 'border border-danger-500/50 bg-danger-500/10 text-danger-500 hover:bg-danger-500/20',
};
const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export const buttonClass = (variant: Variant = 'primary', size: Size = 'md', extra = '') =>
  `inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 ${VARIANTS[variant]} ${SIZES[size]} ${extra}`;

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean }>(
  ({ variant = 'primary', size = 'md', loading, className = '', children, disabled, type = 'button', ...rest }, ref) => (
    <button ref={ref} type={type} className={buttonClass(variant, size, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export function ButtonLink({ variant = 'primary', size = 'md', className = '', ...rest }: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin text-current ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden />;
}

export function Badge({ children, tone = 'neutral', className = '' }: { children: ReactNode; tone?: 'neutral' | 'blue' | 'ok' | 'warn' | 'danger'; className?: string }) {
  const tones = {
    neutral: 'border-ink-500 bg-ink-800 text-steel-200',
    blue: 'border-volt-500/40 bg-volt-500/10 text-volt-300',
    ok: 'border-ok-500/40 bg-ok-500/10 text-ok-500',
    warn: 'border-warn-500/40 bg-warn-500/10 text-warn-500',
    danger: 'border-danger-500/40 bg-danger-500/10 text-danger-500',
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold tracking-wide ${tones[tone]} ${className}`}>{children}</span>;
}

export function Kicker({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 flex items-center gap-3 text-[11px] font-bold tracking-[0.24em] text-volt-400 uppercase">
      <span className="h-px w-10 bg-gradient-to-r from-volt-500 to-volt-300 rtl:bg-gradient-to-l" aria-hidden />
      {children}
    </p>
  );
}

export function EmptyState({ title, body, action, icon }: { title: string; body?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="panel flex flex-col items-center px-6 py-14 text-center" role="status">
      <div className="mb-4 text-steel-500">{icon ?? <SearchIcon className="h-10 w-10" />}</div>
      <h3 className="text-lg font-semibold text-steel-100">{title}</h3>
      {body && <p className="mt-2 max-w-md text-sm text-steel-400">{body}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const { t } = useTranslation();
  const message = error instanceof ApiError ? error.message : t('common.unknownError');
  return (
    <div className={`panel flex flex-col items-center text-center ${compact ? 'px-4 py-6' : 'px-6 py-14'}`} role="alert">
      <div className="mb-3 rounded-full border border-danger-500/40 bg-danger-500/10 p-3 text-danger-500" aria-hidden>
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 8v5m0 3h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-sm text-steel-200">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

export function PlaceholderNotice({ show, className = '' }: { show: boolean; className?: string }) {
  const { t } = useTranslation();
  if (!show) return null;
  return <Badge tone="warn" className={className}>{t('common.placeholderBadge')}</Badge>;
}

export function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (page: number) => void }) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;
  const pages = new Set<number>([1, totalPages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= totalPages));
  const list = [...pages].sort((a, b) => a - b);
  return (
    <nav className="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label={t('common.page', { page, total: totalPages })}>
      <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <Chevron className="h-4 w-4 rtl:rotate-180" dir="left" />
        {t('common.previous')}
      </Button>
      {list.map((p, i) => (
        <span key={p} className="flex items-center gap-2">
          {i > 0 && p - list[i - 1]! > 1 && <span className="text-steel-500">…</span>}
          <button
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`h-8 min-w-8 rounded-lg px-2 text-sm font-semibold transition-colors ${p === page ? 'bg-volt-500 text-white' : 'text-steel-300 hover:bg-ink-800'}`}
          >
            {p}
          </button>
        </span>
      ))}
      <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        {t('common.next')}
        <Chevron className="h-4 w-4 rtl:rotate-180" dir="right" />
      </Button>
    </nav>
  );
}

/** Accessible side drawer (mobile filters / navigation). */
/**
 * Slide-in panel (mobile menu, catalogue filters). Glass navy shell, display title with a blue
 * underline, close button opposite the title, optional sticky footer (Apply / Reset) and optional
 * parts artwork fading in at the bottom (menu).
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  side = 'start',
  footer,
  artwork = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  side?: 'start' | 'end';
  footer?: ReactNode;
  artwork?: boolean;
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previous?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`drawer-shell absolute inset-y-0 flex w-[min(26rem,94vw)] flex-col overflow-hidden outline-none ${side === 'start' ? 'start-0 border-e' : 'end-0 border-s'} animate-fade-in`}
      >
        {artwork && (
          <img
            src="/images/catalogue-banner-800.webp"
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[42%] w-full object-cover object-left opacity-80 [mask-image:linear-gradient(to_top,black_50%,transparent)]"
          />
        )}
        <div className="relative flex items-start justify-between px-5 pt-5 pb-3">
          <div>
            <h2 id={titleId} className="display-caps text-2xl font-bold text-white">
              {title}
            </h2>
            <span aria-hidden className="mt-1.5 block h-0.5 w-12 rounded-full bg-volt-400 shadow-[0_0_10px_rgb(46_139_255/0.9)]" />
          </div>
          <button type="button" onClick={onClose} className="-me-1 rounded-xl p-2 text-steel-200 transition-colors hover:bg-white/5 hover:text-white" aria-label={t('common.close')}>
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="relative flex-1 overflow-y-auto px-4 pt-2 pb-6">{children}</div>
        {footer && <div className="relative border-t border-white/10 bg-ink-950/70 px-4 py-4 backdrop-blur-md">{footer}</div>}
      </div>
    </div>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="rounded-md p-1 text-steel-500 transition-colors hover:bg-ink-700 hover:text-steel-100"
      aria-label={label ?? t('product.copyCode')}
      title={copied ? t('common.copied') : (label ?? t('product.copyCode'))}
    >
      {copied ? (
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-ok-500" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
      )}
    </button>
  );
}

// ---------------------------------------------------------------- form fields
interface FieldShell {
  label: string;
  hint?: string;
  error?: string;
}

export const TextField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldShell>(({ label, hint, error, id, className = '', ...rest }, ref) => {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div className={className}>
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <input ref={ref} id={fieldId} className="field" aria-invalid={error ? true : undefined} aria-describedby={hint || error ? `${fieldId}-hint` : undefined} {...rest} />
      {(hint || error) && (
        <p id={`${fieldId}-hint`} className={`mt-1 text-xs ${error ? 'text-danger-500' : 'text-steel-500'}`}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
TextField.displayName = 'TextField';

export function SelectField({ label, hint, id, className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & FieldShell) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div className={className}>
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <select id={fieldId} className="field appearance-none bg-[length:16px] bg-[position:right_0.75rem_center] bg-no-repeat pe-9 rtl:bg-[position:left_0.75rem_center]" style={{ backgroundImage: CHEVRON_BG }} {...rest}>
        {children}
      </select>
      {hint && <p className="mt-1 text-xs text-steel-500">{hint}</p>}
    </div>
  );
}

export function TextAreaField({ label, hint, id, className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldShell) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div className={className}>
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <textarea id={fieldId} className="field min-h-24" {...rest} />
      {hint && <p className="mt-1 text-xs text-steel-500">{hint}</p>}
    </div>
  );
}

export function Checkbox({ label, checked, onChange, id }: { label: string; checked: boolean; onChange: (v: boolean) => void; id?: string }) {
  const autoId = useId();
  return (
    <label htmlFor={id ?? autoId} className="flex cursor-pointer items-center gap-2.5 text-sm text-steel-200">
      <input id={id ?? autoId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-ink-500 bg-ink-900 accent-volt-500" />
      {label}
    </label>
  );
}

const CHEVRON_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238a94a3' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`;

// ---------------------------------------------------------------- icons
export function SearchIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}
export function CloseIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}
export function Chevron({ className = 'h-4 w-4', dir = 'right' }: { className?: string; dir?: 'left' | 'right' | 'down' }) {
  const d = dir === 'left' ? 'm15 18-6-6 6-6' : dir === 'down' ? 'm6 9 6 6 6-6' : 'm9 18 6-6-6-6';
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d={d} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function CarIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M5 16H3v-4l2-5h14l2 5v4h-2M5 16a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m6 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m-10 0h6M3 12h18" strokeLinejoin="round" />
    </svg>
  );
}
export function UserIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20a8 8 0 0 1 16 0" strokeLinecap="round" />
    </svg>
  );
}
export function CartIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.5L21 8H6.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="10" cy="20" r="1.3" />
      <circle cx="17" cy="20" r="1.3" />
    </svg>
  );
}
export function GridIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="4" y="4" width="6.5" height="6.5" rx="1.5" />
      <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" />
      <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5" />
    </svg>
  );
}
export function FactoryIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M3 21V9l5 3V9l5 3V9l5 3v9H3Zm4-4h2m3 0h2m3 0h2M14 9V4h4v5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function CalendarIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4m8-4v4" strokeLinecap="round" />
    </svg>
  );
}
export function LayersIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="m12 4 8 4-8 4-8-4 8-4Zm-8 8 8 4 8-4M4 16l8 4 8-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function FilterIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round" />
    </svg>
  );
}

export function HomeIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="m3.5 11 8.5-7 8.5 7v9h-6v-5h-5v5h-6z" strokeLinejoin="round" />
    </svg>
  );
}
export function InfoIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
    </svg>
  );
}
export function PhoneIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" strokeLinejoin="round" />
    </svg>
  );
}
export function TagIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M3 12V4h8l9 9-8 8-9-9Z" strokeLinejoin="round" />
      <circle cx="7.5" cy="8.5" r="1.3" />
    </svg>
  );
}
export function GearIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v3M12 18.5v3M21.5 12h-3M5.5 12h-3M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1M18.7 18.7l-2.1-2.1M7.4 7.4 5.3 5.3" strokeLinecap="round" />
    </svg>
  );
}
export function LogoutIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4M15 8l4 4-4 4M19 12H9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function LoginIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M9 8l4 4-4 4M13 12H3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function ShockIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M8 3h4M10 3v3m0 0-3 2 6 2-6 2 6 2-6 2 3 2v2m-2 0h4M15 5v14M13 5h4M13 19h4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function SteeringIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
      <path d="M3.5 10.5 9 12m6 0 5.5-1.5M12 15v5.5" strokeLinecap="round" />
    </svg>
  );
}
export function CheckIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
      <path d="m5 12.5 4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
export function PositionIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v4m0 11v4M2.5 12h4m11 0h4" strokeLinecap="round" />
    </svg>
  );
}
export function PriceIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="2.5" y="6.5" width="19" height="11" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" strokeLinecap="round" />
    </svg>
  );
}
